/**
 * Component Skeletons
 * Specialized loading states matching the real components structure
 * UI/UX Enhancement: Correct layout mirroring for smoother loading transitions
 */

import { Box, Grid, Skeleton, Stack, useTheme, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { tokens } from '../design-system';

const SkeletonBase: React.FC<{ children: React.ReactNode; height?: number | string }> = ({ 
  children, 
  height 
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const borderColor = isDark ? tokens.colors.glass.dark.border : tokens.colors.semantic.border;
  const bgColor = isDark ? tokens.colors.dark.surface : tokens.colors.gray[0];

  return (
    <Box
      sx={{
        height: height || '100%',
        padding: '20px',
        borderRadius: tokens.radius.lg,
        backgroundColor: bgColor,
        border: `1px solid ${borderColor}`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {children}
    </Box>
  );
};

export const ServiceCardSkeleton: React.FC = () => (
  <SkeletonBase height={280}>
    <Stack spacing={2.5} sx={{ height: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="70%" height={32} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="50%" height={20} />
        </Box>
        <Skeleton variant="rounded" width={100} height={28} sx={{ borderRadius: tokens.radius.full }} />
      </Box>

      {/* Uptime Grid matching ServiceCard 2x2 layout */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
        <Grid container spacing={1.5}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Grid item xs={6} key={i}>
              <Box 
                sx={{ 
                  p: 1.5, 
                  borderRadius: tokens.radius.md,
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                  height: 80,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Skeleton variant="text" width={30} height={20} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width={60} height={32} />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Stack>
  </SkeletonBase>
);

export const StatsCardSkeleton: React.FC = () => (
  <SkeletonBase height={160}>
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Skeleton variant="text" width="40%" height={20} />
        <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: tokens.radius.md }} />
      </Box>
      <Box>
        <Skeleton variant="text" width="60%" height={48} />
        <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: tokens.radius.sm, mt: 1 }} />
      </Box>
    </Box>
  </SkeletonBase>
);

export const IncidentCardSkeleton: React.FC = () => (
  <SkeletonBase height={140}>
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Skeleton variant="text" width="30%" height={28} />
        <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: tokens.radius.full }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Skeleton variant="rounded" width={100} height={24} sx={{ borderRadius: tokens.radius.sm }} />
        <Skeleton variant="rounded" width={100} height={24} sx={{ borderRadius: tokens.radius.sm }} />
        <Skeleton variant="rounded" width={100} height={24} sx={{ borderRadius: tokens.radius.sm }} />
      </Box>
      <Skeleton variant="text" width="90%" height={20} />
    </Stack>
  </SkeletonBase>
);

export const ChartSkeleton: React.FC<{ height?: number }> = ({ height = 300 }) => (
  <SkeletonBase height={height}>
    <Stack spacing={2} sx={{ height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Skeleton variant="text" width="30%" height={32} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="rounded" width={60} height={32} />
          <Skeleton variant="rounded" width={60} height={32} />
        </Box>
      </Box>
      <Box sx={{ flex: 1, position: 'relative' }}>
        {/* Mock Y-axis lines */}
        <Stack spacing={4} sx={{ height: '100%', justifyContent: 'space-between' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="text" width="100%" height={1} sx={{ opacity: 0.2 }} />
          ))}
        </Stack>
        {/* Mock Chart Area */}
        <Skeleton 
          variant="rectangular" 
          width="100%" 
          height="70%" 
          sx={{ 
            position: 'absolute', 
            bottom: 0, 
            left: 0, 
            opacity: 0.1,
            borderRadius: tokens.radius.md 
          }} 
        />
      </Box>
    </Stack>
  </SkeletonBase>
);

export const KPICardSkeleton: React.FC = () => (
  <SkeletonBase height={180}>
    <Stack spacing={2} sx={{ height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Skeleton variant="text" width="50%" height={20} />
        <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: tokens.radius.md }} />
      </Box>
      <Box sx={{ mt: 'auto !important' }}>
        <Skeleton variant="text" width="70%" height={48} />
        <Skeleton variant="rounded" width="40%" height={24} sx={{ mt: 1, borderRadius: tokens.radius.sm }} />
      </Box>
    </Stack>
  </SkeletonBase>
);

export const TableSkeleton: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const borderColor = isDark ? tokens.colors.glass.dark.border : tokens.colors.semantic.border;
  const bgColor = isDark ? tokens.colors.dark.surface : tokens.colors.gray[0];

  return (
    <Box
      sx={{
        borderRadius: tokens.radius.lg,
        backgroundColor: bgColor,
        border: `1px solid ${borderColor}`,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 2, borderBottom: `1px solid ${borderColor}` }}>
        <Skeleton variant="text" width={200} height={32} />
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableCell key={i}>
                  <Skeleton variant="text" width={i === 0 ? 150 : 80} height={24} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                {Array.from({ length: 5 }).map((_, j) => (
                  <TableCell key={j}>
                    <Skeleton variant="text" width="100%" height={20} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export const PageHeaderSkeleton: React.FC = () => (
  <Box sx={{ mb: 4 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <Box>
        <Skeleton variant="text" width={200} height={40} />
        <Skeleton variant="text" width={300} height={24} sx={{ mt: 1 }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Skeleton variant="rounded" width={100} height={36} sx={{ borderRadius: tokens.radius.md }} />
      </Box>
    </Box>
  </Box>
);

export const DashboardSkeleton: React.FC = () => (
  <Box sx={{ maxWidth: 1400, margin: '0 auto', width: '100%', px: { xs: 2, md: 3 } }}>
    <PageHeaderSkeleton />
    
    {/* Status Summary Skeleton */}
    <Box sx={{ mb: 4, height: 120, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', borderRadius: tokens.radius.lg }} />

    {/* Stats Cards Skeleton */}
    <Grid container spacing={2} sx={{ mb: 4 }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Grid item xs={12} sm={6} md={3} key={i}>
          <StatsCardSkeleton />
        </Grid>
      ))}
    </Grid>
    
    {/* Service Cards Skeleton */}
    <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
      <Box sx={{ flex: 1, height: 40, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', borderRadius: tokens.radius.md }} />
      <Box sx={{ width: 300, height: 40, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', borderRadius: tokens.radius.md }} />
    </Box>
    <Grid container spacing={2}>
      {Array.from({ length: 6 }).map((_, i) => (
        <Grid item xs={12} sm={6} md={4} key={i}>
          <ServiceCardSkeleton />
        </Grid>
      ))}
    </Grid>
  </Box>
);

// Backward compatibility or generic usage
export const LoadingSkeleton: React.FC<{ count?: number }> = () => {
  return <DashboardSkeleton />;
};

export default LoadingSkeleton;
